import { applyPatch } from "fast-json-patch";
import { wealthProApi } from "../../redux/api";

const vulnerabilitiesApiEndpoints = wealthProApi.injectEndpoints({
    endpoints: (builder) => ({
        createVulnerabilitySection: builder.mutation({
            query: ({ masterAccountId }) => ({
                url: `api/factfind/${masterAccountId}/vulnerabilities`,
                method: 'POST',
                body: {
                    masterAccountId
                }
            }),
            async onQueryStarted({ masterAccountId }, { dispatch, queryFulfilled }) {
                const { data: response } = await queryFulfilled;
                const { vulnerabilitySection, clientVulnerability } = response;
                const sectionId = vulnerabilitySection?.vulnerabilitySectionId;

                if (sectionId && clientVulnerability)
                    try {
                        dispatch(wealthProApi.util.upsertQueryData('fetchVulnerabilitySection', { masterAccountId }, vulnerabilitySection));

                        dispatch(wealthProApi.util.upsertQueryData('fetchClientVulnerabilities', { sectionId, masterAccountId }, clientVulnerability));

                    } catch (err) {
                        console.error("Error creating vulnerability section and client vulnerability in cache", err);
                    }
            }
        }),

        patchVulnerabilitySection: builder.mutation({
            query: ({ vulnerabilitySectionId, operations, masterAccountId }) => ({
                url: `api/factfind/${masterAccountId}/vulnerabilities/${vulnerabilitySectionId}`,
                method: 'PATCH',
                body: operations
            })
        }),

        fetchClientVulnerabilities: builder.query({
            query: ({ vulnerabilitySectionId, masterAccountId, page, limit }) => {
                if (page || limit) {
                    return `api/factfind/${masterAccountId}/vulnerabilities/${vulnerabilitySectionId}/clientVulnerabilities?page=${page ?? 1}&limit=${limit ?? 25}`;
                }

                return `api/factfind/${masterAccountId}/vulnerabilities/${vulnerabilitySectionId}/clientVulnerabilities`;
            }
        }),

        createClientVulnerability: builder.mutation({
            query: ({ vulnerabilitySectionId, masterAccountId }) => ({
                url: `api/factfind/${masterAccountId}/vulnerabilities/vulnerability`,
                method: 'POST',
                body: {
                    vulnerabilitySectionId
                }
            }),
            async onQueryStarted({ vulnerabilitySectionId, masterAccountId }, { dispatch, queryFulfilled }) {
                try {
                    let createdIndex;
                    const createResult = dispatch(wealthProApi.util.updateQueryData('fetchClientVulnerabilities', { vulnerabilitySectionId, masterAccountId }, data => {
                        const newClientVulnerability = {
                            vulnerabilitySectionId,
                            vulnerabilityId: data.vulnerabilityId,
                            clientAccountId: null,
                            clientName: null,
                            vulnerabilityTypeId: null,
                            vulnerabilityTypeText: null,
                            category: null,
                            vulnerabilityCategoryText: null,
                            additionalInfo: null
                        };
                        createdIndex = data.length;
                        return [...data, newClientVulnerability];
                    }))

                    try {
                        const { data: response } = await queryFulfilled;
                        dispatch(wealthProApi.util.invalidateTags(['factFindVulnerabilitySection']));
                        dispatch(wealthProApi.util.updateQueryData('fetchClientVulnerabilities', { vulnerabilitySectionId, masterAccountId }, data => {
                            const newData = [...data.slice(0, createdIndex),
                            {
                                ...data[createdIndex],
                                vulnerabilityId: response.clientVulnerabilityId
                            },
                            ...data.slice(createdIndex + 1)]
                            return newData;
                        }));
                    } catch (err) {
                        console.error("Error creating client vulnerability in service", err);
                        createResult.undo();
                        return;
                    }
                } catch (err) {
                    console.error("Error creating client vulnerability in cache", err);
                }
            }
        }),

        deleteClientVulnerability: builder.mutation({
            query: ({ clientVulnerabilityId, vulnerabilitySectionId, masterAccountId }) => ({
                url: `api/factfind/${masterAccountId}/vulnerabilities/${vulnerabilitySectionId}/vulnerability/${clientVulnerabilityId}`,
                method: 'DELETE',
            }),
            async onQueryStarted({ clientVulnerabilityId, vulnerabilitySectionId, masterAccountId }, { dispatch, queryFulfilled }) {
                try {
                    const deleteResult = dispatch(wealthProApi.util.updateQueryData('fetchClientVulnerabilities', { vulnerabilitySectionId, masterAccountId }, data =>
                        data.filter(x => x.vulnerabilityId !== clientVulnerabilityId)));

                    try {
                        await queryFulfilled;
                        dispatch(wealthProApi.util.invalidateTags(['factFindVulnerabilitySection']));
                    } catch (err) {
                        console.error("Error deleting client vulnerability in service", err);
                        deleteResult.undo();
                    }
                } catch (err) {
                    console.error("Error deleting client vulnerability in cache", err);
                }
            }
        }),
        patchClientVulnerability: builder.mutation({
            query: ({ clientVulnerabilityId, operations, masterAccountId }) => ({
                url: `api/factfind/${masterAccountId}/vulnerabilities/vulnerability/${clientVulnerabilityId}`,
                method: 'PATCH',
                body: operations
            }),
            async onQueryStarted({ vulnerabilitySectionId, clientVulnerabilityId, operations, masterAccountId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(wealthProApi.util.updateQueryData('fetchClientVulnerabilities', { vulnerabilitySectionId, masterAccountId }, data => {
                        var item = data.find(x => x.vulnerabilityId === clientVulnerabilityId);
                        applyPatch(item, operations, true);
                    }))
                }
                catch { }
            }
        }),


    })
});

export const {
    useCreateVulnerabilitySectionMutation,
    usePatchVulnerabilitySectionMutation,
    useCreateClientVulnerabilityMutation,
    useDeleteClientVulnerabilityMutation,
    usePatchClientVulnerabilityMutation,
    useFetchClientVulnerabilitiesQuery,
    useLazyFetchClientVulnerabilitiesQuery
} = vulnerabilitiesApiEndpoints;